import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Club {
  id: string;
  name: string;
  logo_url: string | null;
  city: string;
  slug: string;
}

// Shared cache key — all callers de-dupe to a single fetch.
const userClubKeys = {
  current: ["user-club"] as const,
  byId: (id: string | null) => ["club", id] as const,
};

async function fetchUserClubId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: profile, error } = await supabase
    .from("profiles")
    .select("club_id")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error) throw error;
  return profile?.club_id ?? null;
}

async function fetchClub(clubId: string): Promise<Club | null> {
  const { data, error } = await supabase
    .from("clubs_public")
    .select("id, name, logo_url, city, slug")
    .eq("id", clubId)
    .eq("is_active", true)
    .maybeSingle();

  if (error) throw error;
  if (!data || !data.id || !data.name || !data.city || !data.slug) return null;
  return {
    id: data.id,
    name: data.name,
    city: data.city,
    slug: data.slug,
    logo_url: data.logo_url,
  };
}

/**
 * Shared hook for the user's current club.
 *
 * Backed by TanStack Query → all callers share a single cached fetch
 * instead of each component running its own useEffect chain.
 * This eliminates the race-conditions where two components would
 * fetch the same club simultaneously and update with stale data.
 */
export const useClubContext = () => {
  const queryClient = useQueryClient();
  const [overrideClubId, setOverrideClubId] = useState<string | null>(null);

  // Step 1: derive the user's club_id from their profile (cached).
  const { data: profileClubId, isLoading: loadingProfile } = useQuery({
    queryKey: userClubKeys.current,
    queryFn: fetchUserClubId,
    staleTime: 60 * 1000,
  });

  const effectiveClubId = overrideClubId ?? profileClubId ?? null;

  // Step 2: fetch the club details (cached separately so different
  // components requesting the same clubId share one network call).
  const { data: currentClub = null, isLoading: loadingClub } = useQuery({
    queryKey: userClubKeys.byId(effectiveClubId),
    queryFn: () => (effectiveClubId ? fetchClub(effectiveClubId) : Promise.resolve(null)),
    enabled: effectiveClubId !== null,
    staleTime: 60 * 1000,
  });

  const setCurrentClubId = (clubId: string | null) => {
    setOverrideClubId(clubId);
  };

  const refreshClub = async () => {
    await queryClient.invalidateQueries({ queryKey: userClubKeys.current });
    if (effectiveClubId) {
      await queryClient.invalidateQueries({ queryKey: userClubKeys.byId(effectiveClubId) });
    }
  };

  // Reset the cache when the user signs out / changes account.
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT" || event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        queryClient.invalidateQueries({ queryKey: userClubKeys.current });
      }
    });
    return () => data.subscription.unsubscribe();
  }, [queryClient]);

  const loading =
    loadingProfile ||
    (effectiveClubId !== null && loadingClub);

  return {
    currentClub,
    loading,
    setCurrentClubId,
    refreshClub,
  };
};

// Helper function to get app branding based on club
export const getAppBranding = (club: Club | null) => {
  if (club) {
    return {
      name: `${club.name} Hussel App`,
      logo: club.logo_url,
      shortName: club.name,
    };
  }
  return {
    name: "De Hussel App",
    logo: null,
    shortName: "De Hussel App",
  };
};
